import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheckIcon as ShieldCheck, WarningIcon as Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";

/**
 * Buyer microchip verification gate before accepting a transfer.
 *
 * The buyer must have physically met the dog (or read a current vet
 * certificate) and confirms the microchip matches what the seller
 * registered. Three wrong attempts in 30 minutes triggers a backend
 * cool-down (HTTP 429).
 *
 * On success, calls onVerified() so the parent can proceed with the
 * accept / pay-and-accept action.
 */
export default function MicrochipVerifyDialog({
  open,
  onOpenChange,
  transferId,
  dogName,
  onVerified,
}) {
  const [chip, setChip] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!chip.trim()) {
      toast.error("Enter the microchip number.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/transfers/${transferId}/verify-microchip`, {
        microchip: chip.trim(),
      });
      toast.success(`Microchip verified — ending in ${r.data.last4 || "****"}`);
      onOpenChange(false);
      setChip("");
      onVerified?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="microchip-verify-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral flex items-center gap-2">
            <ShieldCheck size={20} weight="duotone" className="text-[#0F8043]" />
            Verify microchip
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-[#FFE2A8] bg-[#FFF8E5] p-3 text-[12.5px] text-[#8A6A00] flex gap-2">
            <Warning size={14} weight="fill" className="flex-shrink-0 mt-[2px]" />
            <p>
              Before accepting{dogName ? <> <strong>{dogName}</strong></> : null}'s
              transfer, scan or read the microchip and enter the number
              below. This confirms you've physically met the dog (or
              received a current vet certificate) and the chip matches
              what the seller registered. Three wrong tries triggers a
              30-minute cool-down.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
                Microchip number
              </Label>
              <Input
                value={chip}
                onChange={(e) => setChip(e.target.value)}
                placeholder="e.g. 985 112 002 134567"
                autoFocus
                autoComplete="off"
                spellCheck={false}
                className="mt-1 rounded-lg font-mono text-base"
                data-testid="microchip-verify-input"
              />
              <p className="text-[11px] text-[#6B6B6B] mt-1.5">
                Spaces, dashes, and casing are ignored — type it however you read it.
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                className="rounded-full bg-[#0F8043] hover:bg-[#0a6634] text-white"
                data-testid="microchip-verify-submit"
              >
                {busy ? "Verifying…" : "Verify microchip"}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
